import { authApiCall } from "./http.hook";
import makeUrl from "./makeUrl";
import authHeader from "@/services/auth-header";
import {extractSubdomainFromBaseUrl} from "@/helpers/realtimeConfig";

export function getSubdomain() {
  const baseUrl = localStorage.getItem("baseUrl") || "";

  return  extractSubdomainFromBaseUrl(baseUrl);
}

const BIYO_QUEUE_URL = typeof process !== "undefined" && process.env && process.env.VUE_APP_BIYO_QUEUE_URL
    ? process.env.VUE_APP_BIYO_QUEUE_URL
    : "https://queue.biyo.co/api/v1/order-queue";

const SYNC_DEDUP_WINDOW_MS = 5000;
const _lastSynced = new Map();
const toNumericOrderId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed && /^\d+$/.test(trimmed)) {
      return Number(trimmed);
    }
  }
  return 0;
};

class DashboardService{
  getSections () {
    return authApiCall(makeUrl("/sections/"), "get");
  }

  getTerminalConfig () {
    return authApiCall(makeUrl("/config/"), "get");
  }

  getKitchenHubConfig () {
    return authApiCall(makeUrl("/kitchenhub/config/"), "get");
  }

  getKitchenHubOrder (kitchenhubOrderId, brandId) {
    return authApiCall(makeUrl(`/kitchenhub/orders/${kitchenhubOrderId}/?brand_id=${brandId}`), "get");
  }

  getTables () {
    return authApiCall(makeUrl("/tables/"), "get");
  }

  getProducts () {
    return authApiCall(makeUrl("/products/"), "get");
  }

  updateProductAvailability (productId, payload) {
    return authApiCall(makeUrl(`/products/${productId}/availability/`), "patch", payload);
  }

  updateProductModifierAvailability (productId, modifierId, payload) {
    return authApiCall(makeUrl(`/products/${productId}/modifiers/${modifierId}/availability/`), "patch", payload);
  }

  getNextProducts (url) {
    return authApiCall(makeUrl(url), "get");
  }

  getCategories () {
    return authApiCall(makeUrl("/categories/"), "get");
  }

  getOrders (payload) {
    return authApiCall(makeUrl("/orders/"), "post", payload);
  }

  getEmployees () {
    return authApiCall(makeUrl("/employees/"), "get");
  }

  getCustomers (page = 1) {
    return authApiCall(makeUrl(`/customers/list/?page=${page}`), "get");
  }

  getCustomersList ({ page = 1, search = null } = {}) {
    const params = new URLSearchParams();
    if (search) {
      params.set('search', search);
    }
    if (page) {
      params.set('page', page);
    }
    return authApiCall(makeUrl(`/customers/list/?${params.toString()}`), "get");
  }

  getCustomerById (id) {
    const params = new URLSearchParams();
    if (id !== undefined && id !== null) {
      params.set('id', String(id));
    }
    return authApiCall(makeUrl(`/customers/?${params.toString()}`), "get");
  }

  getNextCustomers (nextUrl) {
    if (!nextUrl) {
      return Promise.resolve({ results: [], next: null, previous: null, count: 0 });
    }

    let endpoint = nextUrl;

    if (typeof nextUrl === 'string' && nextUrl.startsWith('http')) {
      const urlObj = new URL(nextUrl);
      endpoint = `${urlObj.pathname}${urlObj.search}`;
    }

    if (endpoint.startsWith('/api/')) {
      endpoint = endpoint.replace('/api', '');
    }

    if (!endpoint.startsWith('/')) {
      endpoint = `/${endpoint}`;
    }

    return authApiCall(makeUrl(endpoint), "get");
  }

  getCurrentShift () {
    return authApiCall(makeUrl("/shifts/sync/"), "get");
  }

  getDiscounts () {
    return authApiCall(makeUrl("/discounts/"), "get");
  }

  createBlankOrder(payload) {
    return authApiCall(makeUrl('/orders/create-blank'), 'post', payload)
  }

  checkOrderIds(payload) {
    // payload: { local_ids: ["...", "..."] }
    return authApiCall(makeUrl('/orders/check-ids/'), 'post', payload);
  }

  async syncOrder(orderPayload) {
    const isArray = Array.isArray(orderPayload);
    const rawOrderId = isArray
      ? (orderPayload[0] && (orderPayload[0].id || orderPayload[0].local_id))
      : (orderPayload.id || orderPayload.local_id);
    const orderId = toNumericOrderId(rawOrderId);
    const updatedAt = isArray
      ? (orderPayload[0] && orderPayload[0].updated_at)
      : orderPayload.updated_at;

    if (orderId) {
      const key = String(orderId);
      const prev = _lastSynced.get(key);
      const now = Date.now();
      if (prev && (now - prev.ts) < SYNC_DEDUP_WINDOW_MS) {
        if (prev.updatedAt === updatedAt) {
          return { success: true, deduplicated: true };
        }
        if (updatedAt && prev.updatedAt && updatedAt < prev.updatedAt) {
          return { success: true, deduplicated: true };
        }
      }
    }

    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      ...authHeader()
    };

    const subdomain = getSubdomain();

    const body = {
      headers,
      order_id: orderId,
      payload: orderPayload,
      subdomain: subdomain || undefined
    };

    try {
      const response = await fetch(BIYO_QUEUE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Queue responded with status: ${response.status}`);
      }

      const data = await response.json().catch(() => ({ success: true }));

      if (orderId) {
        _lastSynced.set(String(orderId), { ts: Date.now(), updatedAt });
      }

      return data;
    } catch (err) {
      console.warn("Biyo queue replicate failed:", err);
      throw err;
    }
  }

  postOrder (payload) {
    return this.syncOrder(payload);
    // return authApiCall(makeUrl("/orders/sync/"), "post", payload);
  }
  postPresignedUrl (payload) {
    return authApiCall(makeUrl("/s3-presigned-url/"), "post", payload);
  }
  postShift (payload) {
    return authApiCall(makeUrl("/shifts/sync/"), "post", payload);
  }

  postCustomer (payload) {
    return authApiCall(makeUrl("/customer-add/"), "post", payload);
  }

  postTablePositions (payload) {
    return authApiCall(makeUrl("/tablepositions/"), "post", payload);
  }

  getDaySummary () {
    return authApiCall(makeUrl("/daysummary/"), "get");
  }

  getOrderList(orderStatus, pageNumber) {
    return authApiCall(makeUrl(`/orderlist?status=${orderStatus}&page=${pageNumber}`),"get")
  }

  getOrder(orderId) {
    // Validate orderId to prevent API calls with undefined
    if (!orderId || orderId === 'undefined') {
      console.error('[getOrder] Invalid orderId:', orderId);
      return Promise.reject(new Error('Invalid orderId: cannot fetch order without valid ID'));
    }
    return authApiCall(makeUrl(`/orderlist?id=${orderId}`),"get")
  }

  getAllRefundedOrders() {
    return authApiCall(makeUrl(`/orderlist?status=5`),"get")
  }

  getNextOrders(nextUrl) {
    const payload = []
    return authApiCall(makeUrl(nextUrl),"post",payload)
  }

  getOnlineOrdersList(orderStatus=0) {
    return authApiCall(makeUrl(`/online/orderlist?status=${orderStatus}`,"get"))
  }
  getOpenOrders() {
    return authApiCall(makeUrl(`/orderlist?status=1`,"get"))
  }

  getRecentOrders() {
    return authApiCall(makeUrl(`/orders/recent`,"get"))
  }

  getCountries(){
    return authApiCall(makeUrl('/countries','get'))
  }

  getUSAStates(){
    return authApiCall(makeUrl('/states','get'))
  }

  deleteAddress(addressId){
    return authApiCall(makeUrl(`/address/${addressId}/`),'delete')
  }

  fetchGiftCardPayments(){
    return authApiCall(makeUrl('/gift-card-payments/'),'get')
  }

  fetchGiftCard(giftCard){
    return authApiCall(makeUrl(`/gift-card/?${giftCard.param}=${giftCard.number}`),'get')
  }

  postGiftCard(giftCard){
    return authApiCall(makeUrl(`/gift-card/`),'post', giftCard)
  }

  updateGiftCard(giftCards){
    return authApiCall(makeUrl(`/gift-card/`), 'patch', giftCards)
  }

  recordGiftCardPayments(giftCardPayments){
    return authApiCall(makeUrl(`/gift-card-payments/record/`), 'post', giftCardPayments)
  }

  addTimeClockEntry(employeeId) {
    return authApiCall(makeUrl("/add-timeclock/"), "post", {employee_id: employeeId});
  }
}

export default new DashboardService();
